import { Component } from "@theme/component";
import { sendMaropostTemplateEmail } from "@theme/maropost-customer-template";
import { getApiLocale } from "@theme/locale-map";
import { trackFormSubmit } from "@theme/analytics";

/**
 * <catalog-share> — Digital Catalog share page.
 *
 * Hosts:
 *   - Copy-link for the catalog banner (handled by
 *     <copy-to-clipboard-component>; this class only sets its `agentid` URL)
 *     and the native-share fallback.
 *   - The "Email the Digital Catalog" form: validates the email, toggles the
 *     submit button, sends the templated email via
 *     `@theme/maropost-customer-template`, and reports success/error inline.
 */

/** @type {RegExp} Basic email validation */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class CatalogShare extends Component {
  requiredRefs = ["emailInput", "sendButton", "errorEl", "successEl"];

  connectedCallback() {
    super.connectedCallback();

    // portal-manager.js may finish loading/initializing before or after this
    // component connects, so cover both orderings: try now (in case it already
    // ran), and again once it signals it's done. Whichever resolves last wins.
    this.#applyPortalIdToShareLinks();
    document.addEventListener("portal-manager:ready", this.#applyPortalIdToShareLinks);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("portal-manager:ready", this.#applyPortalIdToShareLinks);
  }

  /**
   * @returns {string} The catalog URL to share, set by the section.
   */
  get shareUrl() {
    return this.dataset.shareUrl || "";
  }

  /**
   * @param {{ portal_id?: string } | null} portalData
   * @returns {string} The active portal's id, falling back to the shop's
   *   `custom.default_portal_id` metafield when PortalManager has none.
   */
  #getPortalId(portalData) {
    return portalData?.portal_id || this.dataset.defaultPortalId || "";
  }

  /**
   * Append the active portal's id as `agentid` to shareUrl.
   * @param {string} portalId
   * @returns {string}
   */
  #buildUrlWithAgentId(portalId) {
    const separator = this.shareUrl.includes("?") ? "&" : "?";
    return `${this.shareUrl}${separator}agentid=${encodeURIComponent(portalId)}`;
  }

  /**
   * Append the active portal's id as `agentid` to the copy-to-clipboard link
   * and the banner preview href, once PortalManager has resolved it (or the
   * shop's default portal id otherwise). No-op if neither is available — always rebuilt from
   * the original shareUrl, so re-running (e.g. on "portal-manager:ready") can't
   * double-append.
   */
  #applyPortalIdToShareLinks = async () => {
    const { copyLinkComponent, bannerLink } = this.refs;
    if (!this.shareUrl) return;

    const portalData = (await window.PortalManager?.getPortalData?.()) || null;
    const portalId = this.#getPortalId(portalData);
    if (!portalId) return;

    const urlWithAgentId = this.#buildUrlWithAgentId(portalId);

    if (copyLinkComponent instanceof Element) {
      copyLinkComponent.setAttribute("text-to-copy", urlWithAgentId);
    }
    if (bannerLink instanceof HTMLAnchorElement) {
      bannerLink.href = urlWithAgentId;
    }
  };

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return EMAIL_REGEX.test(email);
  }

  #showError(message) {
    const { errorEl } = this.refs;
    errorEl.textContent = message;
    errorEl.classList.remove("tw:hidden");
  }

  #hideError() {
    const { errorEl } = this.refs;
    errorEl.textContent = "";
    errorEl.classList.add("tw:hidden");
  }

  /**
   * Fallback consultant name when PortalManager has no `presented_by`, read from
   * the server-rendered `[data-shop-consultant-name]` hook already in the DOM.
   * @returns {string}
   */
  #getConsultantNameFromDom() {
    return document.querySelector("[data-shop-consultant-name]")?.textContent?.trim() || "";
  }

  /** Enable/disable the submit button as the visitor types. */
  handleEmailInput = () => {
    const { emailInput, sendButton } = this.refs;
    sendButton.disabled = !this.#isValidEmail(emailInput.value.trim());
    this.#hideError();
  };

  /** "More" share — native share sheet on supporting devices, otherwise falls back to copying the link. */
  handleMoreShare = async () => {
    if (!this.shareUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({ url: this.shareUrl });
        return;
      } catch {
        // Cancelled or unsupported for this content — fall through to copy.
      }
    }

    try {
      await navigator.clipboard.writeText(this.shareUrl);
    } catch {
      // Clipboard API unavailable — nothing more we can do silently here.
    }
  };

  /**
   * Handle the "Send Catalog" form submission.
   * @param {SubmitEvent} event
   */
  handleSubmit = async event => {
    event.preventDefault();

    const { emailInput, sendButton, successEl } = this.refs;
    const email = emailInput.value.trim();

    if (!email) {
      this.#showError(this.dataset.validationEmailRequired || "Email is required.");
      return;
    }
    if (!this.#isValidEmail(email)) {
      this.#showError(this.dataset.validationEmailInvalid || "Please enter a valid email address.");
      return;
    }

    const portalData = (await window.PortalManager?.getPortalData?.()) || null;
    const portalId = this.#getPortalId(portalData);
    const { languageCode, countryCode } = getApiLocale();

    this.#hideError();
    successEl.classList.add("tw:hidden");
    sendButton.disabled = true;

    const payload = {
      email: {
        campaign_id: Number(this.dataset.emailCampaignId),
        content_id: Number(this.dataset.emailContentId),
        ignore_dnm: "true",
        subject: this.dataset.emailSubject,
        from_name: this.dataset.emailFromName,
        from_email: this.dataset.emailFromEmail,
        contact: {
          email,
        },
        tags: {
          portal_name: portalId,
          portal_owner_email: portalData?.email_address || this.dataset.emailFromEmail,
          portal_owner_name: portalData?.presented_by || this.#getConsultantNameFromDom(),
          digital_catalog_url: portalId ? this.#buildUrlWithAgentId(portalId) : this.shareUrl,
          site_info: {
            country: countryCode,
          },
          language_selected: languageCode,
        },
      },
    };

    const result = await sendMaropostTemplateEmail(payload);

    if (result.ok) {
      trackFormSubmit(event.target.closest("form"));
      successEl.classList.remove("tw:hidden");
    } else {
      this.#showError(result.error?.message || this.dataset.errorMessage || "Something went wrong. Please try again.");
    }

    sendButton.disabled = false;
  };
}

if (!customElements.get("catalog-share")) {
  customElements.define("catalog-share", CatalogShare);
}
